import React from 'react';
import {
  TableWrapper,
  LoadingSpinner,
  ErrorMessage,
  EmptyState,
  ServerPagination
} from './AdminTableComponents';

// Users Section Component
const UsersSection = ({
  users,
  loading,
  error,
  pagination,
  setPagination,
  search,
  setSearch,
  roleFilter,
  setRoleFilter,
  statusFilter,
  setStatusFilter,
  loadUsers,
  getStatusBadge,
  setSelectedUser,
  setShowAssignModal,
  setSelectedConsultantId,
  assignmentRequests,
  assignmentRequestsLoading,
  handleAssignConsultant,
  consultants
}) => {
  return (
    <div>
      <div className="md:flex md:items-center md:justify-between mb-6">
        <div>
          <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
            Users Management
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            View and manage all registered users
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button
            onClick={loadUsers}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Pending Assignment Requests Section */}
      {assignmentRequests.length > 0 && (
        <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
              <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                Pending Consultant Assignment Requests
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-100">
                  {assignmentRequests.length}
                </span>
              </h4>
            </div>
          </div>

          <div className="space-y-3">
            {assignmentRequestsLoading ? (
              <div className="text-sm text-blue-600 dark:text-blue-400">
                Loading requests...
              </div>
            ) : (
              assignmentRequests.map((request) => (
                <div
                  key={request.id}
                  className="bg-white dark:bg-gray-800 rounded-md p-3 border border-blue-100 dark:border-blue-800"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                            <span className="text-emerald-600 dark:text-emerald-400 font-medium text-xs">
                              {request.user_name?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {request.user_name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {request.user_email}
                          </p>
                          {request.user_company && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {request.user_company}
                            </p>
                          )}
                        </div>
                      </div>
                      {request.notes && (
                        <p className="mt-2 text-xs text-gray-600 dark:text-gray-400 italic">
                          "{request.notes}"
                        </p>
                      )}
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                        Requested {new Date(request.created_at).toLocaleString()}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        // Find user in the users list and prepare assignment
                        const user =
                          users.find((u) => u.id === request.user_id) || {
                            id: request.user_id,
                            name: request.user_name,
                            email: request.user_email,
                            company: request.user_company
                          };
                        setSelectedUser({ ...user, requestId: request.id });
                        setSelectedConsultantId('');
                        setShowAssignModal(true);
                      }}
                      className="ml-4 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
                    >
                      Assign Consultant
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <input
          type="text"
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-emerald-500 focus:border-emerald-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
        />
      </div>

      {error && <ErrorMessage message={error} />}

      {loading ? (
        <LoadingSpinner message="Loading users..." />
      ) : users.length === 0 ? (
        <EmptyState
          icon="👥"
          title="No users found"
          description="Try adjusting your search or filters."
        />
      ) : (
        <>
          <TableWrapper>
            <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    User
                  </th>

                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Consultant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Joined
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {users.map((user) => (
                  <tr
                    key={user.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                            <span className="text-emerald-600 dark:text-emerald-400 font-medium text-sm">
                              {user.name?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {user.name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {user.consultant_name ? (
                        <div>
                          <div className="text-gray-900 dark:text-white">
                            {user.consultant_name}
                          </div>
                          <div className="text-gray-500 dark:text-gray-400 text-xs">
                            {user.consultant_email}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500 italic">
                          Not assigned
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {user.role === 'user' && (
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setSelectedConsultantId(user.consultant_id || '');
                            setShowAssignModal(true);
                          }}
                          className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-900 dark:hover:text-emerald-300"
                        >
                          {user.consultant_id ? 'Change' : 'Assign'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrapper>
          <ServerPagination pagination={pagination} setPagination={setPagination} />
        </>
      )}
    </div>
  );
};

export default UsersSection;


